/**
 * WhatsApp-style reply tools for internal conversations:
 * emoji, images, files, and voice notes.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Popover,
  Stack,
  TextareaAutosize,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import SendIcon from "@mui/icons-material/Send";
import { useTranslation } from "react-i18next";
import {
  colorTokens,
  radiusTokens,
  shadowTokens,
} from "@/widgets/frontdesk/design-system";

export type InternalAttachmentKind = "image" | "file" | "voice";

export interface InternalPendingAttachment {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  kind: InternalAttachmentKind;
  dataUrl: string;
  previewUrl?: string;
}

const EMOJIS = [
  "😀", "😁", "😂", "🙂", "😉", "😍", "🤔", "👍", "👎", "👏",
  "🙏", "🔥", "✨", "✅", "❌", "🎉", "📎", "📷", "🎤", "💬",
];

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_VOICE_BYTES = 5 * 1024 * 1024;
const MAX_ATTACHMENTS = 5;

function fileToDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRecordingTime(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

/** Animated WhatsApp-style waveform bars */
const VoiceWaveform: React.FC<{
  active?: boolean;
  barCount?: number;
  color?: string;
  height?: number;
}> = ({
  active = true,
  barCount = 24,
  color = colorTokens.error[500],
  height = 28,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "2.5px",
      height,
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
    }}
  >
    {Array.from({ length: barCount }).map((_, i) => {
      const base = 0.35 + ((i * 17) % 7) / 10;
      return (
        <Box
          key={i}
          sx={{
            width: 2.5,
            borderRadius: 1,
            backgroundColor: color,
            opacity: active ? 0.85 : 0.45,
            height: `${Math.round(base * 100)}%`,
            transformOrigin: "center",
            animation: active
              ? `voiceWave ${0.7 + (i % 5) * 0.12}s ease-in-out ${i * 0.04}s infinite alternate`
              : "none",
            "@keyframes voiceWave": {
              "0%": { transform: "scaleY(0.35)" },
              "100%": { transform: "scaleY(1)" },
            },
          }}
        />
      );
    })}
  </Box>
);

const RecordingBar: React.FC<{
  seconds: number;
  onCancel: () => void;
  onStop: () => void;
  compact?: boolean;
}> = ({ seconds, onCancel, onStop, compact }) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        gap: compact ? 1 : 1.25,
        px: compact ? 1.25 : 1.5,
        py: compact ? 0.75 : 1,
        minHeight: compact ? 44 : 48,
        borderRadius: compact ? 6 : `${radiusTokens.lg}px`,
        backgroundColor: colorTokens.error[50],
        border: `1px solid ${colorTokens.error[200]}`,
      }}
    >
      <IconButton
        size="small"
        onClick={onCancel}
        aria-label={t("frontdesk.compose.cancelRecording", {
          defaultValue: "Cancel recording",
        })}
        sx={{
          color: colorTokens.neutral[600],
          backgroundColor: "#fff",
          border: `1px solid ${colorTokens.neutral[200]}`,
          width: 32,
          height: 32,
          flexShrink: 0,
          "&:hover": {
            backgroundColor: colorTokens.neutral[100],
            color: colorTokens.error[600],
          },
        }}
      >
        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
      </IconButton>

      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: colorTokens.error[500],
          flexShrink: 0,
          animation: "recPulse 1s ease-in-out infinite",
          "@keyframes recPulse": {
            "0%, 100%": { opacity: 1, transform: "scale(1)" },
            "50%": { opacity: 0.45, transform: "scale(0.85)" },
          },
        }}
      />

      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.875rem",
          color: colorTokens.error[700],
          fontVariantNumeric: "tabular-nums",
          minWidth: 40,
          flexShrink: 0,
        }}
      >
        {formatRecordingTime(seconds)}
      </Typography>

      <VoiceWaveform active barCount={compact ? 18 : 28} height={compact ? 24 : 30} />

      <IconButton
        size="small"
        onClick={onStop}
        aria-label={t("frontdesk.compose.stopRecording", {
          defaultValue: "Stop recording",
        })}
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          backgroundColor: colorTokens.error[500],
          color: "#fff",
          "&:hover": { backgroundColor: colorTokens.error[600] },
        }}
      >
        <StopIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  );
};

const VoiceAttachmentChip: React.FC<{
  attachment: InternalPendingAttachment;
  onRemove: () => void;
  compact?: boolean;
}> = ({ attachment, onRemove, compact }) => {
  const durationMatch = attachment.name.match(/\((\d+)s\)/);
  const durationLabel = durationMatch
    ? formatRecordingTime(Number(durationMatch[1]))
    : null;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.25,
        py: 0.75,
        minWidth: compact ? 180 : 220,
        maxWidth: compact ? 260 : 300,
        borderRadius: 999,
        backgroundColor: colorTokens.primary[50],
        border: `1px solid ${colorTokens.primary[100]}`,
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          backgroundColor: "var(--theme-primary-color, #2E3092)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <MicIcon sx={{ fontSize: 16 }} />
      </Box>
      <VoiceWaveform
        active={false}
        barCount={compact ? 12 : 16}
        height={20}
        color="var(--theme-primary-color, #2E3092)"
      />
      {durationLabel ? (
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 600,
            color: colorTokens.neutral[700],
            fontVariantNumeric: "tabular-nums",
            flexShrink: 0,
          }}
        >
          {durationLabel}
        </Typography>
      ) : null}
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{
          width: 22,
          height: 22,
          color: colorTokens.neutral[500],
          flexShrink: 0,
        }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
};

interface InternalReplyComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (
    content: string,
    attachments: InternalPendingAttachment[],
  ) => Promise<void>;
  disabled?: boolean;
  isSending?: boolean;
  /** Compact pill composer for the floating internal-chat popup */
  variant?: "default" | "popup";
}

export const InternalReplyComposer: React.FC<InternalReplyComposerProps> = ({
  value,
  onChange,
  onSend,
  disabled = false,
  isSending = false,
  variant = "default",
}) => {
  const { t } = useTranslation();
  const dir: "rtl" | "ltr" = t("direction");
  const isRTL = dir === "rtl";
  const isPopup = variant === "popup";

  const [attachments, setAttachments] = useState<InternalPendingAttachment[]>(
    [],
  );
  const [error, setError] = useState<string | null>(null);
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingSecondsRef = useRef(0);
  const discardRecordingRef = useRef(false);

  const canSend = useMemo(
    () =>
      (value.trim().length > 0 || attachments.length > 0) &&
      !disabled &&
      !isSending &&
      !isRecording,
    [value, attachments.length, disabled, isSending, isRecording],
  );

  useEffect(() => {
    return () => {
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const addFiles = useCallback(
    async (files: FileList | File[], preferredKind?: InternalAttachmentKind) => {
      setError(null);
      const list = Array.from(files);
      if (!list.length) return;

      const next: InternalPendingAttachment[] = [];
      for (const file of list) {
        if (attachments.length + next.length >= MAX_ATTACHMENTS) {
          setError(
            t("frontdesk.directMessages.maxAttachments", {
              defaultValue: `You can attach up to ${MAX_ATTACHMENTS} files`,
              count: MAX_ATTACHMENTS,
            }),
          );
          break;
        }

        const isImage = file.type.startsWith("image/");
        const isAudio = file.type.startsWith("audio/");
        const kind: InternalAttachmentKind =
          preferredKind ||
          (isImage ? "image" : isAudio ? "voice" : "file");

        const max =
          kind === "image"
            ? MAX_IMAGE_BYTES
            : kind === "voice"
              ? MAX_VOICE_BYTES
              : MAX_FILE_BYTES;

        if (file.size > max) {
          setError(
            t("frontdesk.directMessages.fileTooLarge", {
              defaultValue: `${file.name} is too large (max ${formatBytes(max)})`,
              name: file.name,
              max: formatBytes(max),
            }),
          );
          continue;
        }

        try {
          const dataUrl = await fileToDataUrl(file);
          next.push({
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            name: file.name || (kind === "voice" ? "Voice message" : "Attachment"),
            mimeType: file.type || "application/octet-stream",
            size: file.size,
            kind,
            dataUrl,
            previewUrl: isImage ? dataUrl : undefined,
          });
        } catch {
          setError(
            t("frontdesk.directMessages.readFileFailed", {
              defaultValue: `Could not read ${file.name}`,
              name: file.name,
            }),
          );
        }
      }

      if (next.length) {
        setAttachments((prev) => [...prev, ...next]);
      }
    },
    [attachments.length, t],
  );

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const insertEmoji = useCallback(
    (emoji: string) => {
      onChange(`${value}${emoji}`);
      setEmojiAnchor(null);
    },
    [onChange, value],
  );

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    setIsRecording(false);
  }, []);

  const cancelRecording = useCallback(() => {
    discardRecordingRef.current = true;
    stopRecording();
    setRecordingSeconds(0);
    recordingSecondsRef.current = 0;
  }, [stopRecording]);

  const startRecording = useCallback(async () => {
    setError(null);
    if (attachments.length >= MAX_ATTACHMENTS) {
      setError(
        t("frontdesk.directMessages.maxAttachments", {
          defaultValue: `You can attach up to ${MAX_ATTACHMENTS} files`,
          count: MAX_ATTACHMENTS,
        }),
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      discardRecordingRef.current = false;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        const shouldDiscard = discardRecordingRef.current;
        discardRecordingRef.current = false;
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        const seconds = Math.max(1, recordingSecondsRef.current);
        recordingSecondsRef.current = 0;
        setRecordingSeconds(0);

        if (shouldDiscard || blob.size === 0) return;
        if (blob.size > MAX_VOICE_BYTES) {
          setError(
            t("frontdesk.directMessages.voiceTooLarge", {
              defaultValue: "Voice message is too large. Try a shorter recording.",
            }),
          );
          return;
        }
        const dataUrl = await fileToDataUrl(blob);
        setAttachments((prev) => [
          ...prev,
          {
            id: `voice-${Date.now()}`,
            name: `Voice message (${seconds}s)`,
            mimeType: blob.type || mimeType,
            size: blob.size,
            kind: "voice",
            dataUrl,
          },
        ]);
      };

      recorder.start();
      setIsRecording(true);
      setRecordingSeconds(0);
      recordingSecondsRef.current = 0;
      recordTimerRef.current = setInterval(() => {
        recordingSecondsRef.current += 1;
        setRecordingSeconds(recordingSecondsRef.current);
      }, 1000);
    } catch {
      setError(
        t("frontdesk.directMessages.micDenied", {
          defaultValue: "Microphone access is required for voice messages",
        }),
      );
    }
  }, [attachments.length, t]);

  const handleSend = useCallback(async () => {
    if (!canSend) return;
    const content = value.trim();
    const pending = [...attachments];
    await onSend(content, pending);
    setAttachments([]);
    setError(null);
  }, [attachments, canSend, onSend, value]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  /** Paste screenshots (e.g. Windows Snipping Tool) as image attachments */
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      if (disabled || isSending || isRecording) return;

      const items = e.clipboardData?.items;
      if (!items?.length) return;

      const imageFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item.type.startsWith("image/")) continue;
        const blob = item.getAsFile();
        if (!blob) continue;

        const ext = (item.type.split("/")[1] || "png").replace(
          /[^a-z0-9]/gi,
          "",
        );
        const file =
          blob instanceof File && blob.name
            ? blob
            : new File([blob], `screenshot-${Date.now()}-${i}.${ext || "png"}`, {
                type: blob.type || `image/${ext || "png"}`,
                lastModified: Date.now(),
              });
        imageFiles.push(file);
      }

      if (imageFiles.length > 0) {
        e.preventDefault();
        void addFiles(imageFiles, "image");
      }
    },
    [addFiles, disabled, isRecording, isSending],
  );

  const iconButtonSx = {
    color: colorTokens.neutral[600],
    width: 34,
    height: 34,
    "&:hover": {
      backgroundColor: colorTokens.neutral[100],
      color: colorTokens.primary[600],
    },
    "&.Mui-disabled": { color: colorTokens.neutral[400] },
  };

  if (isPopup) {
    return (
      <Box
        sx={{
          direction: dir,
          backgroundColor: "#fff",
          py: 0.25,
          // Send sits on the inline-end side of the row (left in RTL, right in LTR)
          ...(dir === "rtl" ? { pl: 0, pr: 0.25 } : { pr: 0, pl: 0.25 }),
        }}
      >
        {attachments.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ px: 0.5, pb: 0.75, flexWrap: "wrap", gap: 1 }}
          >
            {attachments.map((att) =>
              att.kind === "image" && att.previewUrl ? (
                <Box
                  key={att.id}
                  sx={{
                    position: "relative",
                    width: 52,
                    height: 52,
                    borderRadius: 1,
                    overflow: "hidden",
                    border: `1px solid ${colorTokens.neutral[200]}`,
                  }}
                >
                  <Box
                    component="img"
                    src={att.previewUrl}
                    alt={att.name}
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeAttachment(att.id)}
                    sx={{
                      position: "absolute",
                      top: 1,
                      right: 1,
                      width: 18,
                      height: 18,
                      backgroundColor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Box>
              ) : att.kind === "voice" ? (
                <VoiceAttachmentChip
                  key={att.id}
                  attachment={att}
                  onRemove={() => removeAttachment(att.id)}
                  compact
                />
              ) : (
                <Chip
                  key={att.id}
                  size="small"
                  icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={att.name}
                  onDelete={() => removeAttachment(att.id)}
                  sx={{ maxWidth: 180 }}
                />
              ),
            )}
          </Stack>
        )}
        {error && (
          <Typography
            variant="caption"
            sx={{ display: "block", px: 0.5, pb: 0.5, color: colorTokens.error[600] }}
          >
            {error}
          </Typography>
        )}
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          onChange={(e) => {
            if (e.target.files) void addFiles(e.target.files, "file");
            e.target.value = "";
          }}
        />
        <input
          ref={imageInputRef}
          type="file"
          hidden
          multiple
          accept="image/*"
          onChange={(e) => {
            if (e.target.files) void addFiles(e.target.files, "image");
            e.target.value = "";
          }}
        />
        <Stack direction="row" alignItems="flex-end" sx={{ direction: dir, gap: 1 }}>
          {isRecording ? (
            <RecordingBar
              seconds={recordingSeconds}
              onCancel={cancelRecording}
              onStop={stopRecording}
              compact
            />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 0.15,
                px: 0.75,
                py: 0.35,
                minHeight: 44,
                borderRadius: 6,
                backgroundColor: colorTokens.neutral[100],
                border: `1px solid ${colorTokens.neutral[200]}`,
                direction: dir,
              }}
            >
              <IconButton
                size="small"
                disabled={disabled || isSending || isRecording}
                onClick={(e) => setEmojiAnchor(e.currentTarget)}
                sx={{ color: colorTokens.neutral[500] }}
              >
                <EmojiEmotionsOutlinedIcon sx={{ fontSize: 22 }} />
              </IconButton>
              {/* Bare TextareaAutosize: the modern theme forces `height: auto !important` on
                  `.MuiInputBase-multiline textarea`, which breaks InputBase autosizing. */}
              <Box
                component={TextareaAutosize}
                maxRows={4}
                value={value}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                disabled={disabled || isSending || isRecording}
                placeholder={t("frontdesk.notifications.internalMessageToast.replyPlaceholder", {
                  defaultValue: "Type a reply",
                })}
                dir={dir}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  px: 0.5,
                  py: 0,
                  m: 0,
                  border: 0,
                  outline: 0,
                  resize: "none",
                  backgroundColor: "transparent",
                  font: "inherit",
                  fontSize: "0.875rem",
                  lineHeight: 1.5,
                  color: colorTokens.neutral[900],
                  textAlign: "start",
                  "&::placeholder": { color: colorTokens.neutral[400], opacity: 1 },
                  "&:disabled": { color: colorTokens.neutral[400], backgroundColor: "transparent" },
                }}
              />
              <IconButton
                size="small"
                disabled={disabled || isSending || isRecording}
                onClick={() => fileInputRef.current?.click()}
                sx={{ color: colorTokens.neutral[500] }}
              >
                <AttachFileIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                size="small"
                disabled={disabled || isSending || isRecording}
                onClick={() => imageInputRef.current?.click()}
                sx={{ color: colorTokens.neutral[500] }}
              >
                <ImageOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                size="small"
                disabled={disabled || isSending}
                onClick={() => void startRecording()}
                sx={{ color: colorTokens.neutral[500] }}
              >
                <MicIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          )}
          <IconButton
            onClick={() => void handleSend()}
            disabled={!canSend}
            aria-label={t("frontdesk.compose.send", { defaultValue: "Send" })}
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              backgroundColor: canSend
                ? "var(--theme-primary-color, #2E3092)"
                : colorTokens.neutral[300],
              color: "#fff",
              "&:hover": {
                backgroundColor: canSend
                  ? "var(--theme-primary-color, #25267A)"
                  : colorTokens.neutral[300],
              },
              "&.Mui-disabled": {
                backgroundColor: colorTokens.neutral[300],
                color: "#fff",
              },
            }}
          >
            {isSending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SendIcon
                sx={{ fontSize: 20, transform: isRTL ? "scaleX(-1)" : "none" }}
              />
            )}
          </IconButton>
        </Stack>
        <Popover
          open={Boolean(emojiAnchor)}
          anchorEl={emojiAnchor}
          onClose={() => setEmojiAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box
            sx={{
              p: 1,
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: 0.5,
              maxWidth: 220,
            }}
          >
            {EMOJIS.map((emoji) => (
              <IconButton
                key={emoji}
                size="small"
                onClick={() => insertEmoji(emoji)}
                sx={{ fontSize: 20 }}
              >
                {emoji}
              </IconButton>
            ))}
          </Box>
        </Popover>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mx: 2,
        mb: 2,
        mt: 1,
        backgroundColor: colorTokens.semantic.background.primary,
        borderRadius: `${radiusTokens["2xl"]}px`,
        border: `1px solid ${colorTokens.neutral[200]}`,
        overflow: "hidden",
        boxShadow: shadowTokens.sm,
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
        "&:focus-within": {
          borderColor: colorTokens.primary[300],
          boxShadow: `0 0 0 3px ${colorTokens.primary[100]}`,
        },
      }}
    >
      {attachments.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            px: 2,
            pt: 1.5,
            flexWrap: "wrap",
            gap: 1,
            backgroundColor: colorTokens.semantic.background.primary,
          }}
        >
          {attachments.map((att) =>
            att.kind === "image" && att.previewUrl ? (
              <Box
                key={att.id}
                sx={{
                  position: "relative",
                  width: 72,
                  height: 72,
                  borderRadius: 1,
                  overflow: "hidden",
                  border: `1px solid ${colorTokens.neutral[200]}`,
                }}
              >
                <Box
                  component="img"
                  src={att.previewUrl}
                  alt={att.name}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeAttachment(att.id)}
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    width: 22,
                    height: 22,
                    backgroundColor: "rgba(0,0,0,0.55)",
                    color: "#fff",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ) : att.kind === "voice" ? (
              <VoiceAttachmentChip
                key={att.id}
                attachment={att}
                onRemove={() => removeAttachment(att.id)}
              />
            ) : (
              <Chip
                key={att.id}
                icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: 16 }} />}
                label={`${att.name} (${formatBytes(att.size)})`}
                onDelete={() => removeAttachment(att.id)}
                sx={{ maxWidth: 260 }}
              />
            ),
          )}
        </Stack>
      )}

      {error && (
        <Typography
          variant="caption"
          sx={{ display: "block", px: 2, pt: 1, color: colorTokens.error[600] }}
        >
          {error}
        </Typography>
      )}

      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1,
          backgroundColor: colorTokens.semantic.background.primary,
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={disabled || isSending || isRecording}
          placeholder={t("frontdesk.notifications.internalMessageToast.replyPlaceholder", {
            defaultValue: "Type a reply",
          })}
          variant="standard"
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: "0.875rem",
              color: colorTokens.neutral[900],
              lineHeight: 1.6,
              backgroundColor: colorTokens.semantic.background.primary,
            },
          }}
        />
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        onChange={(e) => {
          if (e.target.files) void addFiles(e.target.files, "file");
          e.target.value = "";
        }}
      />
      <input
        ref={imageInputRef}
        type="file"
        hidden
        multiple
        accept="image/*"
        onChange={(e) => {
          if (e.target.files) void addFiles(e.target.files, "image");
          e.target.value = "";
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.5,
          py: 1,
          borderTop: `1px solid ${colorTokens.neutral[200]}`,
          backgroundColor: colorTokens.neutral[50],
          direction: dir,
          gap: 1,
        }}
      >
        {isRecording ? (
          <RecordingBar
            seconds={recordingSeconds}
            onCancel={cancelRecording}
            onStop={stopRecording}
          />
        ) : (
          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flex: 1 }}>
            <Tooltip
              title={t("frontdesk.compose.emoji", { defaultValue: "Emoji" })}
            >
              <span>
                <IconButton
                  size="small"
                  disabled={disabled || isSending || isRecording}
                  onClick={(e) => setEmojiAnchor(e.currentTarget)}
                  sx={iconButtonSx}
                >
                  <EmojiEmotionsOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={t("frontdesk.compose.attachFile", {
                defaultValue: "Attach file",
              })}
            >
              <span>
                <IconButton
                  size="small"
                  disabled={disabled || isSending || isRecording}
                  onClick={() => fileInputRef.current?.click()}
                  sx={iconButtonSx}
                >
                  <AttachFileIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={t("frontdesk.compose.attachImage", {
                defaultValue: "Attach image (or paste screenshot)",
              })}
            >
              <span>
                <IconButton
                  size="small"
                  disabled={disabled || isSending || isRecording}
                  onClick={() => imageInputRef.current?.click()}
                  sx={iconButtonSx}
                >
                  <ImageOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={t("frontdesk.compose.voiceMessage", {
                defaultValue: "Voice message",
              })}
            >
              <span>
                <IconButton
                  size="small"
                  disabled={disabled || isSending}
                  onClick={() => void startRecording()}
                  sx={iconButtonSx}
                >
                  <MicIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )}

        <Button
          variant="contained"
          size="small"
          disabled={!canSend}
          onClick={() => void handleSend()}
          {...(isRTL
            ? {
                endIcon: isSending ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SendIcon sx={{ fontSize: 16, transform: "scaleX(-1)" }} />
                ),
              }
            : {
                startIcon: isSending ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SendIcon sx={{ fontSize: 16 }} />
                ),
              })}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8125rem",
            px: 2,
            py: 0.75,
            borderRadius: `${radiusTokens.md}px`,
            backgroundColor: colorTokens.primary[600],
            boxShadow: "none",
            flexShrink: 0,
            ...(isRTL && {
              flexDirection: "row",
              direction: "rtl",
            }),
            "&:hover": {
              backgroundColor: colorTokens.primary[700],
              boxShadow: "none",
            },
            "&:disabled": {
              backgroundColor: colorTokens.neutral[300],
              color: colorTokens.neutral[500],
            },
            "& .MuiButton-startIcon": {
              marginRight: isRTL ? 0 : 0.75,
              marginLeft: isRTL ? 0.75 : 0,
            },
            "& .MuiButton-endIcon": {
              marginLeft: isRTL ? 0 : 0.75,
              marginRight: isRTL ? 0.75 : 0,
            },
          }}
        >
          {t("frontdesk.compose.send", { defaultValue: "Send" })}
        </Button>
      </Stack>

      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box
          sx={{
            p: 1,
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 0.5,
            maxWidth: 220,
          }}
        >
          {EMOJIS.map((emoji) => (
            <IconButton
              key={emoji}
              size="small"
              onClick={() => insertEmoji(emoji)}
              sx={{ fontSize: 20 }}
            >
              {emoji}
            </IconButton>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};
